import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit2,
  Trash2,
  EyeOff,
  MapPin,
  Tag,
  Calendar,
  Package,
  FileText,
  Inbox,
  AlertTriangle,
} from "lucide-react";
import {
  getMarketplaceProfile,
  getMyListings,
  getMyRequirements,
  deleteListing,
  deleteRequirement,
  unpublishListing,
  MarketplaceListingListItem,
  MarketplaceRequirement,
  MarketplaceProfile,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import MarketplaceLoader from "./MarketplaceLoader";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { resolveImage } from "../../../Utils/helper";
import { isProfileCompleted } from "./MarketplaceShared";
import {
  LISTING_STATUS_BADGE_STYLES,
  LISTING_TYPE_BADGE_STYLES,
  PRICE_UNIT_LABELS,
} from "../../organisms/Dashboard/Marketplace/marketplaceConstants";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";

const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

type ActiveTab = "listings" | "requirements";

const MarketplaceMyListingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<ActiveTab>("listings");
  const [listings, setListings] = useState<MarketplaceListingListItem[]>([]);
  const [requirements, setRequirements] = useState<MarketplaceRequirement[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);
  const [myProfile, setMyProfile] = useState<MarketplaceProfile | null>(null);

  
  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      navigate("/marketplace");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      }, 100);
      return;
    }
    getMarketplaceProfile()
      .then((res) => {
        const profileData = res?.data || null;
        if (!isProfileCompleted(profileData)) {
          navigate("/marketplace/profile/create");
        } else {
          setMyProfile(profileData);
          localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
          setAuthChecking(false);
        }
      })
      .catch(() => {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const prof = parsed?.data || parsed;
            if (isProfileCompleted(prof)) {
              setMyProfile(prof);
              setAuthChecking(false);
              return;
            }
          } catch {}
        }
        navigate("/marketplace/profile/create");
      });
  }, []);

  const fetchListings = async () => {
    setLoadingListings(true);
    try {
      const res = await getMyListings({ page_size: 50 });
      setListings(res.results ?? []);
    } catch {
      setListings([]);
    } finally {
      setLoadingListings(false);
    }
  };

  const fetchRequirements = async () => {
    setLoadingReqs(true);
    try {
      const res = await getMyRequirements({ page_size: 50 });
      setRequirements(res.results ?? []);
    } catch {
      setRequirements([]);
    } finally {
      setLoadingReqs(false);
    }
  };

  useEffect(() => {
    if (!authChecking) {
      fetchListings();
      fetchRequirements();
    }
  }, [authChecking]);

  const handleDeleteListing = async (id: string, listing_type?: string) => {
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot delete listings.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this listing?")) return;
    setActionId(id);
    try {
      await deleteListing(id);
      sendFirebaseEvent('Marketplace_Listing_Deleted', 'Marketplace', listing_type || 'unknown', id);
      showSuccessToast("Listing deleted");
      await fetchListings();
    } catch {
      showErrorToast("Failed to delete listing");
    } finally {
      setActionId(null);
    }
  };

  const handleUnpublish = async (id: string, listing_type?: string) => {
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot modify listings.");
      return;
    }
    setActionId(id);
    try {
      await unpublishListing(id);
      sendFirebaseEvent('Marketplace_Listing_Unpublished', 'Marketplace', listing_type || 'unknown', id);
      showSuccessToast("Listing unpublished");
      await fetchListings();
    } catch {
      showErrorToast("Failed to unpublish");
    } finally {
      setActionId(null);
    }
  };

  const handleDeleteReq = async (id: string, req_type?: string) => {
    if (myProfile?.is_active === false) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot delete requirements.");
      return;
    }
    if (!window.confirm("Delete this requirement?")) return;
    setActionId(id);
    try {
      await deleteRequirement(id);
      sendFirebaseEvent('Marketplace_Requirement_Deleted', 'Marketplace', req_type || 'unknown', id);
      showSuccessToast("Requirement deleted");
      await fetchRequirements();
    } catch {
      showErrorToast("Failed to delete requirement");
    } finally {
      setActionId(null);
    }
  };

  if (authChecking || loadingListings || loadingReqs) {
    return <MarketplaceLoader fullScreen />;
  }

  return (
    <PageLayout>
      <ContentWrap>
          {/* Header */}
        <PageHeader>
          <HeaderLeft>
            <PageTitle>My Listings</PageTitle>
            <PageSubtitle>Manage your listings and requirements</PageSubtitle>
          </HeaderLeft>
        </PageHeader>

        {myProfile?.is_active === false && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 18px",
              marginBottom: "20px",
              borderRadius: "10px",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              color: "#fca5a5",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            <AlertTriangle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
            <div>
              <strong>Account Frozen:</strong> Your marketplace account has been deactivated/frozen by an administrator.
              Your listings and requirements are currently inactive, and actions (create, edit, delete, publish) are disabled.
            </div>
          </div>
        )}

        {/* Tabs */}
        <TabBar>
          <Tab $active={tab === "listings"} onClick={() => setTab("listings")}>
            <Package size={15} />
            My Listings
            <TabCount>{listings.length}</TabCount>
          </Tab>
          <Tab $active={tab === "requirements"} onClick={() => setTab("requirements")}>
            <FileText size={15} />
            My Requirements
            <TabCount>{requirements.length}</TabCount>
          </Tab>
        </TabBar>

        {/* Listings Tab */}
        {tab === "listings" && (
          <>
            {listings.length === 0 ? (
              <EmptyState>
                <Inbox size={40} color="rgba(23,23,23,0.18)" />
                <EmptyTitle>No listings yet</EmptyTitle>
                <EmptyText>Create your first listing to start selling or renting.</EmptyText>
                <GradientBtn onClick={() => navigate("/marketplace/sell")}>
                  <Plus size={15} /> Add Your First Listing
                </GradientBtn>
              </EmptyState>
            ) : (
              <CardGrid>
                {listings.map((l) => {
                  const typeInfo = LISTING_TYPE_BADGE_STYLES[l.listing_type] ?? LISTING_TYPE_BADGE_STYLES.rent;
                  const statusInfo = LISTING_STATUS_BADGE_STYLES[l.status] ?? LISTING_STATUS_BADGE_STYLES.draft;
                  return (
                    <ListingCard key={l.id}>
                      {(l.image_url || l.cover_image || (l.images && l.images[0]?.image_url)) ? (
                        <CardImage src={resolveImage(l.image_url || l.cover_image || (l.images && l.images[0]?.image_url) || "")} alt={l.title} />
                      ) : (
                        <CardImagePlaceholder>
                          <Package size={28} color="rgba(23,23,23,0.15)" />
                        </CardImagePlaceholder>
                      )}
                      <CardBody>
                        <CardTopRow>
                          <TypeBadge style={{ background: typeInfo.bg, color: typeInfo.color }}>
                            {typeInfo.label}
                          </TypeBadge>
                          <StatusBadge style={{ background: statusInfo.bg, color: statusInfo.color }}>
                            {l.status}
                          </StatusBadge>
                        </CardTopRow>
                        <CardTitle>{l.title}</CardTitle>
                        <CardMeta>
                          {l.location && (
                            <MetaRow>
                              <MapPin size={12} /> {l.location}
                            </MetaRow>
                          )}
                          {l.price && (
                            <MetaRow>
                              <Tag size={12} /> ₹{l.price}{" "}
                              <span style={{ opacity: 0.6 }}>
                                {PRICE_UNIT_LABELS[l.price_unit] ?? ""}
                              </span>
                            </MetaRow>
                          )}
                        </CardMeta>
                        <CardActions>
                          <ActionBtn
                            onClick={() => navigate(`/marketplace/sell?id=${l.id}`)}
                            $variant="edit"
                          >
                            <Edit2 size={12} /> Edit
                          </ActionBtn>
                          {l.status === "active" && (
                            <ActionBtn
                              onClick={() => handleUnpublish(l.id)}
                              disabled={actionId === l.id}
                              $variant="unpublish"
                            >
                              <EyeOff size={12} />
                              {actionId === l.id ? "…" : "Unpublish"}
                            </ActionBtn>
                          )}
                          <ActionBtn
                            onClick={() => handleDeleteListing(l.id)}
                            disabled={actionId === l.id}
                            $variant="delete"
                          >
                            <Trash2 size={12} />
                            {actionId === l.id ? "…" : "Delete"}
                          </ActionBtn>
                        </CardActions>
                      </CardBody>
                    </ListingCard>
                  );
                })}
              </CardGrid>
            )}
          </>
        )}

        {/* Requirements Tab */}
        {tab === "requirements" && (
          <>
            {requirements.length === 0 ? (
              <EmptyState>
                <FileText size={40} color="rgba(23,23,23,0.18)" />
                <EmptyTitle>No requirements posted yet</EmptyTitle>
                <EmptyText>Post what equipment or service you need.</EmptyText>
                <GradientBtn onClick={() => navigate("/marketplace/post-requirement")}>
                  <Plus size={15} /> Post Your First Requirement
                </GradientBtn>
              </EmptyState>
            ) : (
              <ReqGrid>
                {requirements.map((req) => {
                  const statusInfo = LISTING_STATUS_BADGE_STYLES[req.status] ?? LISTING_STATUS_BADGE_STYLES.draft;
                  return (
                    <ReqCard key={req.id}>
                      <ReqTop>
                        <ReqTypeBadge $type={req.requirement_type}>
                          {req.requirement_type === "equipment_requirement"
                            ? "📷 Equipment"
                            : req.requirement_type === "bulk_custom_unit"
                            ? "👥 Custom Unit Quote"
                            : "🎬 Service"}
                        </ReqTypeBadge>
                        <StatusBadge style={{ background: statusInfo.bg, color: statusInfo.color }}>
                          {req.status}
                        </StatusBadge>
                      </ReqTop>
                      <ReqTitle>
                        {req.title}
                        {req.reference && (
                          <span style={{ fontSize: "11px", fontWeight: 700, color: "#c94daa", marginLeft: "8px" }}>
                            ({req.reference})
                          </span>
                        )}
                      </ReqTitle>
                      {req.description && <ReqDesc>{req.description}</ReqDesc>}
                      <ReqMetaRow>
                        {req.location && (
                          <MetaItem>
                            <MapPin size={11} /> {req.location}
                          </MetaItem>
                        )}
                        {(req.date_from || req.date_to) && (
                          <MetaItem>
                            <Calendar size={11} /> {req.date_from || "—"} → {req.date_to || "—"}
                          </MetaItem>
                        )}
                        {req.budget_range ? (
                          <MetaItem>💰 {req.budget_range.replace(/_/g, " ").toUpperCase()}</MetaItem>
                        ) : req.budget ? (
                          <MetaItem>💰 ₹{req.budget}</MetaItem>
                        ) : null}
                      </ReqMetaRow>
                      <CardActions>
                        <ActionBtn
                          $variant="edit"
                          onClick={() => navigate("/marketplace/received-applications")}
                          style={{ borderColor: "rgba(201, 77, 170, 0.3)", color: "#c94daa" }}
                        >
                          <Inbox size={12} /> Applications
                        </ActionBtn>
                        <ActionBtn
                          onClick={() =>
                            navigate(`/marketplace/post-requirement?edit=${req.id}`)
                          }
                          $variant="edit"
                        >
                          <Edit2 size={12} /> Edit
                        </ActionBtn>
                        <ActionBtn
                          onClick={() => handleDeleteReq(req.id)}
                          disabled={actionId === req.id}
                          $variant="delete"
                        >
                          <Trash2 size={12} />
                          {actionId === req.id ? "…" : "Delete"}
                        </ActionBtn>
                      </CardActions>
                    </ReqCard>
                  );
                })}
              </ReqGrid>
            )}
          </>
        )}
      </ContentWrap>
    </PageLayout>
  );
};

export default MarketplaceMyListingsPage;

// ─── Styled Components ─────────────────────────────────────────────────────────

const PageWrap = styled.div`
  min-height: 100vh;
  background: #f7f8fc;
  font-family: Urbanist, sans-serif;
`;

const PageLayout = styled.div`
  min-height: calc(100vh - 64px);
  width: 100%;
  background: var(--mp-bodyBackground, #f7f8fc);
`;

const ContentWrap = styled.div`
  width: 100%;
  box-sizing: border-box;
  max-width: 1680px;
  margin: 0 auto;
  padding: 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: ${fadeUp} 0.35s ease both;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 80px 20px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: var(--mp-subColor, rgba(23, 23, 23, 0.4));
`;

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`display: flex; flex-direction: column; gap: 4px;`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: var(--mp-textColor, #171717);
`;

const PageSubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-subColor, rgba(23, 23, 23, 0.5));
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const GradientBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  height: 40px; padding: 0 18px; border: none; border-radius: 10px;
  background: ${gradient}; color: white;
  font-family: Urbanist, sans-serif; font-size: 13px; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(201,77,170,0.22);
  transition: opacity 0.2s, transform 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
`;

const OutlineBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  height: 40px; padding: 0 18px;
  border: 1.5px solid rgba(201,77,170,0.3); border-radius: 10px;
  background: var(--mp-cardBackground, white); color: #c94daa;
  font-family: Urbanist, sans-serif; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(201,77,170,0.05); }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23,23,23,0.08));
  border-radius: 10px;
  overflow: hidden;
  width: fit-content;
`;

const Tab = styled.button<{ $active: boolean }>`
  display: flex; align-items: center; gap: 7px;
  height: 44px; padding: 0 20px;
  border: none; cursor: pointer;
  font-family: Urbanist, sans-serif; font-size: 14px; font-weight: 700;
  background: ${(p) => (p.$active ? "var(--mp-iconGrad, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%))" : "var(--mp-cardBackground, white)")};
  color: ${(p) => (p.$active ? "white" : "var(--mp-subColor, rgba(23,23,23,0.6))")};
  transition: all 0.2s;
  &:hover { background: ${(p) => p.$active ? "var(--mp-iconGrad, linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%))" : "var(--mp-bodyBackground, #f7f8fc)"}; }
`;

const TabCount = styled.span`
  background: rgba(255,255,255,0.25);
  padding: 2px 7px; border-radius: 20px;
  font-size: 11px; font-weight: 700;
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: 12px; padding: 60px 24px;
  background: var(--mp-cardBackground, white); border-radius: 16px;
  border: 1px solid var(--mp-border, rgba(23,23,23,0.07));
`;

const EmptyTitle = styled.h3`
  margin: 0; font-size: 18px; font-weight: 800; color: var(--mp-subColor, rgba(23,23,23,0.5));
`;

const EmptyText = styled.p`
  margin: 0; font-family: Montserrat, sans-serif; font-size: 13px;
  color: var(--mp-subColor, rgba(23,23,23,0.4)); text-align: center;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px)  { grid-template-columns: 1fr; }
`;

const ListingCard = styled.div`
  background: var(--mp-cardBackground, white); border: 1px solid var(--mp-border, rgba(23,23,23,0.07));
  border-radius: 12px; overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  display: flex; flex-direction: column;
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover { box-shadow: 0 8px 24px rgba(0,0,0,0.1); transform: translateY(-2px); }
`;

const CardImage = styled.img`
  width: 100%; height: 150px; object-fit: cover; display: block;
`;

const CardImagePlaceholder = styled.div`
  width: 100%; height: 150px; background: var(--mp-bodyBackground, #f7f8fc);
  display: flex; align-items: center; justify-content: center;
`;

const CardBody = styled.div`
  padding: 14px; display: flex; flex-direction: column; gap: 10px; flex: 1;
`;

const CardTopRow = styled.div`
  display: flex; align-items: center; gap: 8px;
`;

const TypeBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 4px 10px;
  border-radius: 6px; font-family: Montserrat, sans-serif;
  text-transform: capitalize;

  html[data-marketplace-theme="dark"] & {
    background: rgba(255, 255, 255, 0.08) !important;
    color: var(--mp-textColor, #ffffff) !important;
    border: 1px solid rgba(255, 255, 255, 0.12);
  }
`;

const StatusBadge = styled.span`
  font-size: 11px; font-weight: 700; padding: 4px 10px;
  border-radius: 6px; font-family: Montserrat, sans-serif;
  margin-left: auto;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  html[data-marketplace-theme="dark"] & {
    background: rgba(255, 255, 255, 0.05) !important;
    color: var(--mp-textColor, #ffffff) !important;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const CardTitle = styled.h3`
  margin: 0; font-size: 15px; font-weight: 800; color: var(--mp-textColor, #171717);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  display: flex; flex-direction: column; gap: 5px;
`;

const MetaRow = styled.div`
  display: flex; align-items: center; gap: 5px;
  font-family: Montserrat, sans-serif; font-size: 11px;
  color: var(--mp-subColor, rgba(23,23,23,0.5)); font-weight: 600;
`;

const CardActions = styled.div`
  display: flex; gap: 7px; flex-wrap: wrap;
  padding-top: 8px; border-top: 1px solid var(--mp-border, rgba(23,23,23,0.05));
  margin-top: auto;

  @media (max-width: 400px) {
    button {
      flex: 1;
      justify-content: center;
    }
  }
`;

const ActionBtn = styled.button<{ $variant: "edit" | "unpublish" | "delete" }>`
  display: flex; align-items: center; gap: 6px;
  height: 32px; padding: 0 12px; border-radius: 8px;
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.03em;
  cursor: pointer; transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  border: 1.5px solid var(--mp-border, rgba(23, 23, 23, 0.12));
  background: var(--mp-cardBackground, white);
  color: var(--mp-textColor, #171717);

  &:hover:not(:disabled) {
    background: var(--mp-bodyBackground, #f7f8fc);
    border-color: var(--mp-textColor, #171717);
    transform: translateY(-1px);
  }

  ${(p) =>
    p.$variant === "edit" &&
    `
    border-color: var(--mp-border, rgba(23, 23, 23, 0.12));
    &:hover:not(:disabled) {
      background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
      color: var(--mp-heroBtnColor, white);
      border-color: var(--mp-featureIconColor, #D863BA);
    }
  `}

  ${(p) =>
    p.$variant === "unpublish" &&
    `
    border-color: var(--mp-border, rgba(23, 23, 23, 0.12));
    &:hover:not(:disabled) {
      background: var(--mp-textColor, #171717);
      color: var(--mp-cardBackground, white);
      border-color: var(--mp-textColor, #171717);
    }
  `}

  ${(p) =>
    p.$variant === "delete" &&
    `
    border-color: rgba(239, 68, 68, 0.25);
    color: #ef4444;
    background: rgba(239, 68, 68, 0.04);
    &:hover:not(:disabled) {
      background: #ef4444;
      color: white;
      border-color: #ef4444;
    }
  `}

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Requirements
const ReqGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const ReqCard = styled.div`
  background: var(--mp-cardBackground, white); border: 1px solid var(--mp-border, rgba(23,23,23,0.07));
  border-radius: 12px; padding: 16px;
  display: flex; flex-direction: column; gap: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.1); transform: translateY(-2px); }
`;

const ReqTop = styled.div`display: flex; align-items: center; gap: 8px;`;

const ReqTypeBadge = styled.span<{ $type: string }>`
  font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px;
  background: ${(p) =>
    p.$type === "equipment_requirement"
      ? "rgba(14,165,233,0.1)"
      : "rgba(139,92,246,0.1)"};
  color: ${(p) =>
    p.$type === "equipment_requirement" ? "#0369a1" : "#6d28d9"};
  font-family: Montserrat, sans-serif;
`;

const ReqTitle = styled.h3`
  margin: 0; font-size: 15px; font-weight: 800; color: var(--mp-textColor, #171717);
`;

const ReqDesc = styled.p`
  margin: 0; font-size: 13px; color: var(--mp-subColor, rgba(23,23,23,0.55)); line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
`;

const ReqMetaRow = styled.div`display: flex; flex-wrap: wrap; gap: 8px;`;

const MetaItem = styled.div`
  display: flex; align-items: center; gap: 4px;
  font-family: Montserrat, sans-serif; font-size: 11px; color: var(--mp-subColor, rgba(23,23,23,0.5)); font-weight: 600;
`;
